import { useTheme } from '@mui/material/styles';
import { useGetWorkItems } from 'features/matching-engine/hooks/useGetWorkItems';
import { Skill } from 'features/skills';
import { WorkItemCardList } from '../ManageSkillDashboardDrawer';
import { AccordionSummary, CardContent, Stack, Typography } from '@mui/material';
import { Badge } from 'common/components/material';
import Accordion, { AccordionDetails } from 'common/components/material/Accordion';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  RoundedEdgeCard,
  ToggleableList,
} from 'features/matching-engine/matching-engine.utils';

export type SkillGroups = {
  matchingSkills?: Skill[];
  missingSkills?: Skill[];
};

export const WorkItemsForSkill: React.FC<{
  skillId: string;
  profileId: string;
}> = ({ skillId, profileId }) => {
  const { data = [], isLoading } = useGetWorkItems({
    skillIds: [skillId],
    profileId,
  });

  if (isLoading)
    return (
      <Typography fontSize="0.85rem" fontStyle="italic">
        Loading...
      </Typography>
    );

  return (
    <Stack spacing={2}>
      <ToggleableList
        items={[{ workItems: data }]}
        initialVisibleCount={2}
        renderItem={(item) => (
          <WorkItemCardList
            profileId={profileId}
            workItemList={item.workItems}
            skillId={skillId}
          />
        )}
      />
    </Stack>
  );
};

export const MatchingSkillsAccordionList: React.FC<{
  skillIds: string[];
  profileId: string;
  selectedSkillId?: string;
  skillsByCategory: SkillGroups;
  canViewActivities?: boolean;
}> = ({ skillIds, profileId, skillsByCategory, selectedSkillId, canViewActivities = true }) => {
  const { data: workItems = [] } = useGetWorkItems({
    skillIds,
    profileId: canViewActivities ? profileId : '',
  });
  const data = canViewActivities ? workItems : [];

  const initiativeCount = data.reduce((acc: Record<string, number>, item: any) => {
    for (const skillId of Object.keys(item.skillContributionsMetadata || {})) {
      acc[skillId] = (acc[skillId] || 0) + 1;
    }
    return acc;
  }, {});

  const { palette } = useTheme();

  const allSkills = skillsByCategory?.matchingSkills || [];

  const orderedSkills = selectedSkillId
    ? [
        ...allSkills.filter((skill) => skill.id === selectedSkillId),
        ...allSkills.filter((skill) => skill.id !== selectedSkillId),
      ]
    : allSkills;

  return (
    <Stack>
      {orderedSkills.map((skill, index) => {
        const isFirstCard = index === 0;
        const isLastCard = index === skillIds.length - 1;

        return (
          <RoundedEdgeCard key={skill.id} isFirstCard={isFirstCard} isLastCard={isLastCard}>
            <CardContent
              sx={{ padding: '0px', marginX: '8px', paddingBottom: '5px !important' }}
            >
              <Accordion
                defaultExpanded={canViewActivities && skill.id === selectedSkillId}
                {...(!canViewActivities && { expanded: false })}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
                // The content of Accordions is mounted by default even if the accordion is not expanded.
                // By enabling the unmountOnExit, we prevent the default behaviour.
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary
                  expandIcon={<NavigateNextIcon />}
                  sx={{
                    paddingX: 0,
                    flexDirection: 'row-reverse',
                    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                      transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      margin: 0,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography fontWeight="600" fontSize="0.95rem">
                      {skill.name}
                    </Typography>
                    {initiativeCount?.[skill.id] && (
                      <Typography variant="caption" color={palette.text.secondary}>
                        {initiativeCount[skill.id]} initiative
                        {initiativeCount[skill.id] > 1 ? 's' : ''} requiring this skill
                      </Typography>
                    )}
                  </Stack>
                </AccordionSummary>

                {canViewActivities && (
                  <AccordionDetails sx={{ pt: 1 }}>
                    <WorkItemsForSkill skillId={skill.id} profileId={profileId} />
                  </AccordionDetails>
                )}
              </Accordion>
            </CardContent>
          </RoundedEdgeCard>
        );
      })}
    </Stack>
  );
};

export const SkillCategorySections: React.FC<{
  label: string;
  badgeElement: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, badgeElement, children }) => {
  const { palette } = useTheme();

  return (
    <Stack>
      <Stack flexDirection="row" spacing={1.5} sx={{ mb: '8px' }}>
        <Typography fontWeight="600" fontSize="1rem" color={palette.Text.Headline}>
          {label}
        </Typography>
        {badgeElement}
      </Stack>
      {children}
    </Stack>
  );
};

export const ProfileSkillBreakdown: React.FC<{
  profileId: string;
  skillGroups: SkillGroups;
  selectedSkillId?: string;
  canViewActivities?: boolean;
}> = ({ profileId, skillGroups, selectedSkillId, canViewActivities = true }) => {
  if (!skillGroups) return <></>;
  const skillIds = (skillGroups?.matchingSkills ?? []).map((s: any) => s.id).filter(Boolean);

  const countOfMissingSkills = (skillGroups?.missingSkills ?? []).length;

  return (
    <Stack spacing={3} m={1}>
      {skillIds.length > 0 && (
        <SkillCategorySections
          label="Matching skills"
          badgeElement={
            <Badge sx={{ pl: 2 }} color={'primary'} badgeContent={skillIds.length} />
          }
        >
          <MatchingSkillsAccordionList
            profileId={profileId}
            skillIds={skillIds}
            selectedSkillId={selectedSkillId}
            skillsByCategory={skillGroups}
            canViewActivities={canViewActivities}
          />
        </SkillCategorySections>
      )}

      {countOfMissingSkills > 0 && (
        <SkillCategorySections
          label="Missing Skills"
          badgeElement={
            <Badge sx={{ pl: 2 }} color={'error'} badgeContent={countOfMissingSkills} />
          }
        >
          {skillGroups?.missingSkills?.map((skill, i) => {
            const isFirst = i === 0;
            const isLast = i === (skillGroups?.missingSkills ?? []).length - 1;

            return (
              <RoundedEdgeCard isFirstCard={isFirst} isLastCard={isLast}>
                <CardContent sx={{ padding: '0px', margin: '9px', pb: '5px !important' }}>
                  <Typography fontWeight="600" fontSize="0.95rem">
                    {skill.name}
                  </Typography>
                </CardContent>
              </RoundedEdgeCard>
            );
          })}
        </SkillCategorySections>
      )}
    </Stack>
  );
};
